import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminNotificationCenterTemplate } from '@/components/templates';
import useAppModal from '@/hooks/useAppModal';
import {
  useClinicNotificationBulkAction,
  useClinicNotificationList,
  useClinicNotificationUnreadCounts,
} from '@/hooks/main/useClinicNotificationCenter';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  downloadClinicDriftCsv,
  downloadClinicRunErrorCsv,
} from '@/services/main/notificationCenter';
import { downloadBlob, messageFromBlobError } from '@/utils/blobDownload';
import {
  NOTIFICATION_TAB,
  DEFAULT_PER_PAGE,
  type NotificationFilters,
  type NotificationItem,
  type NotificationTab,
} from '@/types/admin-notification-center';

/**
 * TWO INBOXES, not three. マスタ同期 is the admin portal's: a clinic does not send 治療院 / 保険者 records,
 * so a tab for them would list an action it cannot take, cannot retry and did not cause. The server refuses
 * ?tab=master on this route as well — leaving it out of this array alone would be a screen-deep decision.
 */
const TABS: NotificationTab[] = [NOTIFICATION_TAB.REZEPT, NOTIFICATION_TAB.INBOUND];

/**
 * 通知センター — the 治療院 portal's copy.
 *
 * SAME SCREEN, ONE CLINIC. It renders the admin template with `showClinicColumns={false}`, so 治療院ID and
 * 治療院名 leave both the grid and the search panel: the reader is inside one clinic, and a column that
 * repeats the same value on every row costs the width 内容 needs. 👤 owner, 2026-09-02.
 *
 * THE SCOPE IS NOT HERE. Nothing on this page narrows anything to a clinic — the server reads clinic_id off
 * the authenticated user for every request (ClinicNotificationCenterController). A screen-side scope would
 * be one hand-typed URL away from being bypassed, and would also have to be re-earned by every future
 * caller of the same endpoints.
 *
 * The filters live in the URL for the same reason they do on the admin screen: this page is shared by
 * pasting a link, and a filter that only exists in memory cannot be sent to anyone.
 */
const ClinicNotificationCenterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const appModal = useAppModal();
  const { tenant, clinicNumber } = useClinicContext();

  const tabParam = searchParams.get('tab') as NotificationTab | null;
  const tab: NotificationTab =
    tabParam && TABS.includes(tabParam) ? tabParam : NOTIFICATION_TAB.REZEPT;
  const page = Math.max(1, Number(searchParams.get('page') || '1'));
  // 20 A PAGE, FIXED. The Hitowa common spec gives every list one page size and no control to change it,
  // so `per_page` is neither read from the URL nor written to it — a hand-typed ?per_page=5000 now changes
  // nothing rather than being validated down.
  const perPage = DEFAULT_PER_PAGE;

  const filters: NotificationFilters = useMemo(
    () => ({
      from: searchParams.get('from') || '',
      to: searchParams.get('to') || '',
      read: (searchParams.get('read') as NotificationFilters['read']) || 'all',
      // 種別 means an OUTCOME on レセプト同期 and WHICH MESSAGE 審査 sent on the other tab, where every row
      // is 情報 — so a value carried across empties the list while the tab badge still shows a count. Same
      // rule as the admin screen, and dropped at READ time because the trap also arrives via a pasted link.
      levels:
        tab === NOTIFICATION_TAB.INBOUND
          ? []
          : (searchParams.getAll('level') as NotificationFilters['levels']),
      // THE MIRROR OF THE LINE ABOVE. 種別 on this tab is the KIND, and it is a separate query key so the
      // two meanings can never overwrite each other in a URL. Dropped on the sending tabs for the same
      // reason `level` is dropped here: their panel shows the other 種別, so a kind carried across would
      // narrow a list with a condition the reader can see no control for.
      kinds:
        tab === NOTIFICATION_TAB.INBOUND
          ? (searchParams.getAll('kind') as NotificationFilters['kinds'])
          : [],
      // Always empty: this screen has no 治療院 controls and the server ignores them on this route.
      clinicNumber: '',
      clinicName: '',
      serviceType: searchParams.get('service') || '',
      patient: searchParams.get('patient') || '',
    }),
    [searchParams, tab]
  );

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const listQuery = { ...filters, tab, page, perPage };
  const { data, isFetching } = useClinicNotificationList(listQuery);
  const { data: unreadCounts } = useClinicNotificationUnreadCounts();
  const bulk = useClinicNotificationBulkAction();

  const rows = useMemo(() => data?.data ?? [], [data]);

  /**
   * Selection is dropped whenever the visible set changes — new tab, new page, new search.
   *
   * A safety rule, not tidiness: the bulk buttons act on an id list, so a tick left over from a page the
   * operator has since navigated away from would be acted on by a press aimed at what is on screen now.
   */
  useEffect(() => {
    setSelectedIds([]);
  }, [tab, page, searchParams]);

  const writeParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams);
      mutate(params);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleTabChange = (next: NotificationTab) =>
    writeParams(params => {
      params.set('tab', next);
      params.set('page', '1');
      // 施術区分 belongs to レセプト同期 and 受診者 to 審査システムからの通知; carrying either across would
      // filter the new tab by a condition its own panel cannot show, let alone clear.
      params.delete('service');
      params.delete('patient');
    });

  const handleFiltersChange = (next: NotificationFilters) =>
    writeParams(params => {
      const put = (name: string, value: string) =>
        value ? params.set(name, value) : params.delete(name);

      put('from', next.from);
      put('to', next.to);
      put('read', next.read === 'all' ? '' : next.read);
      put('patient', next.patient.trim());

      params.delete('level');
      next.levels.forEach(value => params.append('level', value));

      params.delete('kind');
      next.kinds.forEach(value => params.append('kind', value));

      put('service', next.serviceType);

      // A narrower search can leave the current page past the end of the result set.
      params.set('page', '1');
    });

  const handleLevelShortcut = (levels: NotificationFilters['levels']) =>
    handleFiltersChange({ ...filters, levels });

  const handlePageChange = (next: number) => writeParams(params => params.set('page', String(next)));

  /* ------------------------------------------------------------------------------ bulk actions */

  const selectedOnPage = rows.filter(item => selectedIds.includes(item.id));
  const retryable = selectedOnPage.filter(item => item.retryable);

  /**
   * `expectedUnread` is what the READER sees, and it is what separates a stale list from an ordinary tick
   * of an 既読 row when the bulk endpoint comes back having moved nothing. Counted off the drawn rows, not
   * off the server, precisely because the server's answer is the thing being interpreted.
   */
  const handleBulkMarkRead = () =>
    bulk.mutate({
      action: 'markRead',
      ids: selectedIds,
      expectedUnread: selectedOnPage.filter(item => !item.read).length,
    });

  /**
   * 再送信 overwrites whatever 審査 currently holds for those records, including anything typed there by
   * hand — the same warning the 同期通知 screens give, for the same reason.
   *
   * There is no 再取込 branch here: the button is hidden on 審査システムからの通知 (nothing on that tab
   * failed), so this text is the only one this screen can show.
   */
  const handleBulkRetry = () =>
    appModal.confirm({
      title: `${retryable.length}件を再送信します`,
      content: (
        <>
          <p>審査システムへ再送信します。現在の内容で上書きされます。よろしいですか？</p>
          <p className="m-0 text-sm text-gray-500">
            審査側で手入力された内容は上書きされますが、審査結果・返戻・入金の情報は保持されます。
          </p>
        </>
      ),
      okText: 'はい',
      cancelText: 'いいえ',
      onOk: () => bulk.mutate({ action: 'retry', ids: retryable.map(item => item.id) }),
    });

  /**
   * Run one download button, and SAY SOMETHING WHEN IT FAILS.
   *
   * 🔴 THE CATCH IS THE POINT. Both handlers below used to hand their promise to nobody: a 404 became an
   * unhandled rejection, no dialog opened, and the row simply did nothing when pressed — indistinguishable
   * from a browser that blocked the download. The server has a sentence for every one of these refusals
   * (BD §CSV 73.0 / 74.0 / 75.0) and they differ in what the reader should do next, so the server's words
   * are preferred and the generic one below is only reached when no response arrived at all.
   */
  const runDownload = async (
    fetchFile: () => Promise<{ blob: Blob; filename: string }>
  ): Promise<void> => {
    try {
      const { blob, filename } = await fetchFile();
      downloadBlob(blob, filename);
    } catch (error) {
      appModal.error({
        content:
          (await messageFromBlobError(error)) ?? 'ファイルをダウンロードできませんでした。',
      });
    }
  };

  /**
   * ① エラー詳細. The file comes from the SERVER, which reads sync_audit_logs — every attempt of every
   * failed rezept. The row on screen only ever carries one summary sentence for the whole press.
   */
  const handleDownloadError = (item: NotificationItem) =>
    runDownload(() => downloadClinicRunErrorCsv(tenant ?? '', clinicNumber ?? '', item.id));

  /**
   * ② 不一致明細 — its own endpoint, not the error one.
   *
   * BD §CSV: this button belongs on the 治療院 portal's 状態の不一致 row, and it was the one download the
   * screen never offered — the endpoint, the file on disk and the table's own button branch all existed,
   * but nothing passed `onDownloadDrift`, so the column rendered 「-」 and a clinic could see a drift count
   * it had no way to read.
   *
   * The button is only drawn where the row says a file exists, so a failure here means the file went away
   * since the page loaded — a clean-up, or a reconcile run that could not write. That is what the server's
   * 「次回の照合後に再度お試しください。」 says, and why its sentence is shown rather than a generic one.
   */
  const handleDownloadDrift = (item: NotificationItem) =>
    runDownload(() => downloadClinicDriftCsv(tenant ?? '', clinicNumber ?? '', item.id));

  return (
    <AdminNotificationCenterTemplate
      breadcrumbItems={[{ title: '通知センター' }]}
      tabs={TABS}
      showClinicColumns={false}
      showAdminContactHint
      tab={tab}
      onTabChange={handleTabChange}
      unreadCounts={unreadCounts ? { ...unreadCounts, master: 0 } : undefined}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      data={rows}
      meta={data?.meta}
      summary={data?.summary}
      isLoading={isFetching}
      onPageChange={handlePageChange}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      onBulkRetry={handleBulkRetry}
      onBulkMarkRead={handleBulkMarkRead}
      onDownloadError={handleDownloadError}
      onDownloadDrift={handleDownloadDrift}
      onLevelShortcut={handleLevelShortcut}
      isBusy={bulk.isPending}
    />
  );
};

export default ClinicNotificationCenterPage;
